import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Scale,
  AlertCircle,
  BookOpen,
  CheckCircle,
  XCircle,
  ChevronRight,
  Download,
  Printer,
  ExternalLink,
  Gavel,
  CreditCard,
  Truck,
  ShieldAlert,
  Info,
} from "lucide-react";

const Terms = () => {
  const [activeSection, setActiveSection] = useState("agreement");

  const sections = [
    {
      id: "agreement",
      icon: <BookOpen className="w-5 h-5" />,
      title: "Agreement to Terms",
      content:
        "By accessing or using Trade Bridge, you agree to be bound by these Terms of Service. These terms constitute a legally binding agreement between you (whether as an individual or on behalf of a business entity) and Trade Bridge regarding your use of our premium wholesale marketplace.",
    },
    {
      id: "accounts",
      icon: <Scale className="w-5 h-5" />,
      title: "Wholesale Accounts",
      content:
        "Access to our B2B marketplace requires a verified professional account. By registering, you represent and warrant that:",
      list: [
        "You have the legal authority to bind your business entity",
        "All provided registration information is accurate and truthful",
        "You will maintain the confidentiality of your account credentials",
        "You are a legitimate business or an authorized representative",
      ],
    },
    {
      id: "products",
      icon: <Info className="w-5 h-5" />,
      title: "Product Information & Listings",
      content:
        "We strive to maintain high accuracy in product information. However, as a multi-supplier wholesale platform:",
      list: [
        "Product images and descriptions are for illustrative purposes",
        "Bulk quantities and packaging may vary slightly between suppliers",
        "Pricing is subject to change based on market conditions and volume",
        "Final availability is confirmed only upon order acceptance",
      ],
    },
    {
      id: "shipping",
      icon: <Truck className="w-5 h-5" />,
      title: "Shipping & Logistics",
      content:
        "Trade Bridge facilitates global shipping through our trusted logistics network:",
      list: [
        "Default shipping terms: FOB (Free On Board) Egypt",
        "Mixed containers, LCL, FCL, and air freight options available",
        "Buyers are responsible for import clearance and local duties",
        "Real-time tracking is provided for all shipments",
      ],
    },
    {
      id: "prohibited",
      icon: <XCircle className="w-5 h-5" />,
      title: "Prohibited Activities",
      content: "You agree not to engage in any of the following activities:",
      list: [
        "Circumventing platform fees or engaging in off-platform transactions",
        "Automated scraping or data mining of supplier information",
        "Providing false or misleading business credentials",
        "Attempting to interfere with the security or functionality of the platform",
      ],
    },
    {
      id: "liability",
      icon: <Gavel className="w-5 h-5" />,
      title: "Limitation of Liability",
      content:
        "Trade Bridge provides the platform on an 'as is' and 'as available' basis. To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the marketplace or any transactions conducted through it.",
    },
  ];

  // Download PDF Handler
  const handleDownloadPDF = () => {
    const fileName = `Trade_Bridge_Terms_of_Service_${new Date().getFullYear()}.pdf`;

    // Simple Blob download (replace with html2pdf.js in production for better formatting)
    const content = `
Trade Bridge - Terms of Service
Effective Date: April 2026 | Version 1.1.0

${sections
  .map(
    (section) =>
      `${section.title}\n\n${section.content}\n\n${
        section.list ? section.list.join("\n• ") : ""
      }\n\n`,
  )
  .join("\n")}

By using Trade Bridge, you acknowledge that this is a B2B wholesale platform and agree to all terms outlined above.
    `;

    const blob = new Blob([content], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log("✅ Terms of Service PDF downloaded");
  };

  const handlePrint = () => {
    window.print();
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 120;
      const elementPosition =
        element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: "smooth",
      });
      setActiveSection(id);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background Layers */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[30%] -left-[15%] w-[70%] h-[70%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl" />
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[85vh] lg:min-h-[75vh] flex items-center justify-center pt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 mb-6">
                <Gavel className="w-4 h-4 text-primary-500" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 tracking-wide">
                  LEGAL FRAMEWORK
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
                <span className="text-gray-900 dark:text-white">Terms of</span>{" "}
                <span className="bg-gradient-to-r from-primary-500 via-primary-600 to-secondary-600 bg-clip-text text-transparent">
                  Service
                </span>
              </h1>

              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto mb-8">
                Effective Date: April 2026 • Version 1.1.0
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleDownloadPDF}
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-medium rounded-2xl shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95"
                >
                  <Download className="w-4 h-4 group-hover:rotate-12 transition-transform" />
                  Download PDF
                </button>

                <button
                  onClick={handlePrint}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl text-gray-700 dark:text-gray-300 font-medium rounded-2xl border border-white/50 dark:border-gray-800/50 hover:bg-white/60 dark:hover:bg-gray-900/60 transition-all"
                >
                  <Printer className="w-4 h-4" />
                  Print Terms
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Sticky Sidebar Navigation */}
          <aside className="lg:w-80 shrink-0">
            <div className="sticky top-24 space-y-2">
              <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 px-4">
                TABLE OF CONTENTS
              </p>

              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full flex items-center justify-between px-5 py-3.5 rounded-2xl text-sm font-medium transition-all ${
                    activeSection === section.id
                      ? "bg-white dark:bg-gray-800 shadow-md text-primary-600 dark:text-primary-400"
                      : "text-gray-600 dark:text-gray-400 hover:bg-white/60 dark:hover:bg-gray-900/60"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {section.icon}
                    <span>{section.title}</span>
                  </div>
                  <ChevronRight
                    size={18}
                    className={`transition-transform ${activeSection === section.id ? "rotate-90" : ""}`}
                  />
                </button>
              ))}

              {/* Help Card */}
              <div className="mt-10 p-6 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-3xl text-white shadow-xl">
                <AlertCircle className="w-8 h-8 mb-3 opacity-90" />
                <h4 className="font-semibold mb-1">Legal Questions?</h4>
                <p className="text-sm text-white/80 mb-4">
                  Our legal and compliance team is available to assist you.
                </p>
                <a
                  href="mailto:admin@tradebridge-export.com"
                  className="inline-flex items-center gap-2 text-sm font-medium bg-white/20 hover:bg-white/30 px-4 py-2 rounded-xl transition-all"
                >
                  Contact Legal Team
                  <ExternalLink size={14} />
                </a>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 max-w-3xl">
            <div className="space-y-16">
              {sections.map((section) => (
                <motion.section
                  key={section.id}
                  id={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="scroll-mt-24"
                >
                  <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-3xl border border-white/30 dark:border-gray-800/30 shadow-xl overflow-hidden">
                    <div className="p-8 md:p-10">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white flex-shrink-0">
                          {section.icon}
                        </div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
                          {section.title}
                        </h2>
                      </div>

                      <div className="prose prose-gray dark:prose-invert max-w-none text-[15px] leading-relaxed">
                        <p className="text-gray-700 dark:text-gray-300">
                          {section.content}
                        </p>

                        {section.list && (
                          <ul className="mt-6 space-y-3">
                            {section.list.map((item, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-3 bg-white/60 dark:bg-gray-800/60 rounded-2xl p-4 border border-white/40 dark:border-gray-700"
                              >
                                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                                <span className="text-gray-700 dark:text-gray-300">
                                  {item}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.section>
              ))}
            </div>

            {/* Important Summary */}
            <div className="mt-20 p-8 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-900 dark:to-gray-800 rounded-3xl border border-amber-100 dark:border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="text-amber-500" />
                <h3 className="text-xl font-bold dark:text-white">
                  Important Notice
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                Trade Bridge is a B2B wholesale marketplace. All transactions
                are commercial in nature. Consumer protection laws applicable to
                retail purchases may not apply. Please review all orders and
                terms carefully before confirming any bulk transaction.
              </p>
            </div>

            {/* Footer Note */}
            <div className="mt-16 text-center text-sm text-gray-500 dark:text-gray-400">
              Trade Bridge • Alexandria, Egypt • +20 111 477 9577 •
              admin@tradebridge-export.com
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Terms;
